import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import './DropdownMenu.css';

/**
 * Componente de Dropdown Menu reutilizável
 * 
 * @param {Object} props
 * @param {Array} props.items - Array de itens do menu. Cada item pode ter:
 *   - icon: string (classe do Bootstrap Icon)
 *   - label: string (texto do item)
 *   - shortcut: string (atalho de teclado, opcional)
 *   - onClick: function (callback ao clicar)
 *   - to: string (rota para Link, opcional)
 *   - disabled: boolean (desabilitar item)
 *   - divider: boolean (adicionar separador antes deste item)
 *   - header: boolean (se for um header)
 *   - customContent: ReactNode (conteúdo customizado)
 * @param {ReactNode} props.toggle - Conteúdo customizado para o toggle
 * @param {string} props.variant - Variante do botão toggle (default: 'outline-primary')
 * @param {string} props.size - Tamanho do botão (default: 'sm')
 * @param {string} props.toggleIcon - Ícone do botão toggle (default: 'three-dots-vertical')
 * @param {string} props.align - Alinhamento do menu ('start' | 'end', default: 'end')
 * @param {string} props.className - Classes CSS adicionais
 * @param {Object} props.menuStyle - Estilos customizados para o menu
 */
function DropdownMenu({ 
    items = [], 
    toggle,
    variant = 'outline-primary', 
    size = 'sm',
    toggleIcon = 'three-dots-vertical',
    align = 'end',
    className = '',
    menuStyle = {}
}) {
    return (
        <Dropdown className={className}>
            {toggle ? (
                <Dropdown.Toggle 
                    as="div" 
                    className="p-0 border-0 bg-transparent dropdown-toggle-no-arrow" 
                    style={{ cursor: 'pointer' }}
                    id={`dropdown-toggle-${Math.random().toString(36).substr(2, 9)}`}
                >
                    {toggle}
                </Dropdown.Toggle>
            ) : (
            <Dropdown.Toggle 
                as="button"
                variant="link"
                size={size}
                className="p-0 border-0 bg-transparent text-dark dropdown-toggle-no-arrow"
                style={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    transition: 'background-color 0.15s ease',
                    outline: 'none',
                    boxShadow: 'none'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onFocus={(e) => {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onBlur={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.boxShadow = 'none';
                }}
            >
                <i className={`bi bi-${toggleIcon}`} style={{ fontSize: '1.25rem', color: '#6c757d' }}></i>
            </Dropdown.Toggle>
            )}
            <Dropdown.Menu 
                align={align} 
                className="shadow-sm border" 
                style={{ minWidth: '200px', borderRadius: '8px', padding: '4px 0', borderColor: '#e9ecef', zIndex: 9999, ...menuStyle }}
            >
                {items.map((item, index) => {
                    // Se for um separador
                    if (item.divider) {
                        return <Dropdown.Divider key={`divider-${index}`} />;
                    }

                    // Se for um header
                    if (item.header) {
                        return (
                            <Dropdown.Header key={`header-${index}`} className={item.className || ''} style={item.style || {}}>
                                {item.label}
                            </Dropdown.Header>
                        );
                    }

                    // Se tiver conteúdo customizado
                    if (item.customContent) {
                        return <div key={`custom-${index}`}>{item.customContent}</div>;
                    }

                    return (
                        <Dropdown.Item
                            key={index}
                            {...(item.to ? { as: Link, to: item.to } : {})}
                            onClick={item.onClick}
                            disabled={item.disabled}
                            className={`d-flex align-items-center px-3 py-2 border-0 ${item.className || ''}`}
                            style={{ 
                                fontSize: '0.875rem',
                                cursor: item.disabled ? 'not-allowed' : 'pointer',
                                color: item.disabled ? '#adb5bd' : '#212529',
                                transition: 'background-color 0.15s ease',
                                backgroundColor: 'transparent',
                                textDecoration: 'none',
                                ...(item.style || {})
                            }}
                            onMouseEnter={(e) => {
                                if (!item.disabled) {
                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                }
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            {item.icon && (
                                <i className={`bi bi-${item.icon} me-3`} style={{ width: '18px', fontSize: '1rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: item.disabled ? '#adb5bd' : '#6c757d' }}></i>
                            )}
                            <span className="flex-grow-1">{item.label}</span>
                            {item.shortcut && (
                                <span className="text-muted ms-3" style={{ fontSize: '0.75rem', fontFamily: 'monospace', opacity: 0.6 }}>
                                    {item.shortcut}
                                </span>
                            )}
                        </Dropdown.Item>
                    );
                })}
            </Dropdown.Menu>
        </Dropdown>
    );
}

export default DropdownMenu;
